import clsx from 'clsx';
import { matchMediaOrSsr, useMediaQuery } from '../hooks/use-media-query.js';
import { rems, px } from '../utils/css.js';
import { fullscreenRoot, size, modalWidthVar, insetWidth, insetXVar, vwWidth, widthVwVar, contentsMaxHeightVar, contentsHeightVar, topAligned, insetHeight, insetYVar, vhHeight, heightVhVar, contentsMaxWidthVar } from './modal.css.js';

const INSET_PREFIX = 'inset-';
function useBreakpoint(size) {
    return useMediaQuery(getBreakpoint(size));
}
/**
 * Resolve the full set of sizing props into the classes and custom properties the root applies,
 * plus the breakpoint that drives the small-screen fullscreen collapse.
 */
function parseSize(width, height, maxWidth, maxHeight, fullscreen) {
    const parsedWidth = parseWidth(width);
    // `height="fullscreen"` is deprecated; it folds onto the boolean prop. Fullscreen takes over
    // both dimensions, so it overrides the parsed width/height sizing entirely.
    if (fullscreen || height === 'fullscreen') {
        return {
            className: fullscreenRoot,
            style: undefined,
            breakpoint: parsedWidth.breakpoint,
        };
    }
    const parsedHeight = parseHeight(height);
    return {
        className: clsx(parsedWidth.className, parsedHeight.className),
        style: { ...parsedWidth.vars, ...parsedHeight.vars, ...parseMax(maxWidth, maxHeight) },
        breakpoint: parsedWidth.breakpoint,
    };
}
// 20rem = 320px = 256px / 0.8
// modal contents takes up at most 80% of the screen height, and 320px is
// a reasonable "smallest" height a mobile devices will ever be (in landscape)
const HEIGHT_BREAKPOINT = 'screen and (max-height: 20rem)';
// 32rem = 512px = 480px + (2 * 16px)
const BREAKPOINT_LG = matchMediaOrSsr(`screen and (max-width: 32rem), ${HEIGHT_BREAKPOINT}`);
// 22rem = 352px = 320px + (2 * 16px)
const BREAKPOINT_MD = matchMediaOrSsr(`screen and (max-width: 22rem), ${HEIGHT_BREAKPOINT}`);
// the small breakpoint would be 17rem = 272px = 240px + (2 * 16px), but that's smaller
// than the smallest mobile device, so we only set up a height breakpoint in this case.
const BREAKPOINT_SM = matchMediaOrSsr(HEIGHT_BREAKPOINT);
function getBreakpoint(size) {
    switch (size) {
        case 'sm':
            return BREAKPOINT_SM;
        case 'md':
            return BREAKPOINT_MD;
        case 'lg':
            return BREAKPOINT_LG;
    }
}
/**
 * Resolve the `width` prop into a class, custom properties, and the breakpoint that decides when
 * the modal collapses to fullscreen on small screens.
 */
function parseWidth(width) {
    if (width in size) {
        const breakpoint = width;
        return { breakpoint, className: size[breakpoint], vars: {} };
    }
    if (width === 'fit-content')
        return { breakpoint: 'lg', vars: {} };
    if (typeof width === 'number') {
        return { breakpoint: 'lg', vars: { [modalWidthVar]: rems(width) } };
    }
    if (width.startsWith(INSET_PREFIX)) {
        return {
            breakpoint: 'lg',
            className: insetWidth,
            vars: { [insetXVar]: px(parseInset(width)) },
        };
    }
    return {
        breakpoint: 'lg',
        className: vwWidth,
        vars: { [widthVwVar]: String(parseFloat(width)) },
    };
}
/** Pull the px count out of an `inset-${n}px` value. */
function parseInset(value) {
    return parseFloat(value.slice(INSET_PREFIX.length));
}
/** Resolve the `height` prop into a class and custom properties. */
function parseHeight(height) {
    // height vars use 'initial' so nested content-sized modals fallback to auto, not parent height
    // `fullscreen` is handled by the caller via the boolean prop; never reached when set.
    if (height === 'fixed' || height === 'fullscreen') {
        return {
            vars: { [contentsHeightVar]: 'initial', [contentsMaxHeightVar]: 'initial' },
        };
    }
    if (height === 'dynamic') {
        return {
            className: topAligned,
            vars: { [contentsHeightVar]: 'initial', [contentsMaxHeightVar]: 'initial' },
        };
    }
    // Normalize the friendly alias down to its base viewport value before parsing.
    const value = height === 'full' ? '80vh' : height;
    if (typeof value === 'number') {
        return {
            vars: { [contentsHeightVar]: rems(value), [contentsMaxHeightVar]: 'none' },
        };
    }
    if (value.startsWith(INSET_PREFIX)) {
        return { className: insetHeight, vars: { [insetYVar]: px(parseInset(value)) } };
    }
    return { className: vhHeight, vars: { [heightVhVar]: String(parseFloat(value)) } };
}
/** Resolve the `maxWidth` / `maxHeight` props into ceiling custom properties on the contents. */
function parseMax(maxWidth, maxHeight) {
    const vars = {};
    if (maxWidth != null)
        vars[contentsMaxWidthVar] = maxLength(maxWidth);
    if (maxHeight != null)
        vars[contentsMaxHeightVar] = maxLength(maxHeight);
    return vars;
}
/**
 * A px ceiling is kept as px (a rems conversion could shrink it below the value); a `vw`/`vh`
 * string is used as-is.
 */
function maxLength(value) {
    return typeof value === 'number' ? px(value) : value;
}

export { parseSize, useBreakpoint };
//# sourceMappingURL=modal.utils.js.map
